// ─────────────────────────────────────────────────────────────────────────────
// FEATURE: Orders  (shared pieces of the order list and the order page)
// StatusBadge (colour + words, never colour alone), OrderLines (photo from the menu, pack · cut, qty, line price) and
// OrderTimeline (the real status history: only steps that happened carry a time; the current step says so; a
// cancelled order shows where it stopped). Data: utils/orders.ts.
// ─────────────────────────────────────────────────────────────────────────────
import { Ban, Check, CircleDot } from "lucide-react";
import { seafoodItems } from "../../data/seafood";
import { packById } from "../../data/packs";
import { formatInr } from "../../utils/priceFormat";
import { STATUS_LABEL, STEPS, formatWhen, reachedAt, type Order, type OrderStatus } from "../../utils/orders";

const BADGE: Record<OrderStatus, string> = {
  placed: "bg-ocean-700/10 text-ocean-900 dark:bg-white/10 dark:text-foam-50",
  confirmed: "bg-teal-100 text-teal-700",
  preparing: "bg-teal-100 text-teal-700",
  out_for_delivery: "bg-teal-100 text-teal-700",
  delivered: "bg-teal-100 text-teal-700",
  cancelled: "bg-coral-400/10 text-coral-500 dark:text-coral-300",
};

export function StatusBadge({ status }: { status: OrderStatus }) {
  return <span className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ${BADGE[status]}`}>{STATUS_LABEL[status]}</span>;
}

export function OrderLines({ order }: { order: Order }) {
  return (
    <ul className="space-y-3">
      {order.items.map((line) => {
        const item = seafoodItems.find((candidate) => candidate.id === line.id);
        return (
          <li key={`${line.id}-${line.pack}`} className="flex gap-3">
            {item ? (
              <img src={item.image} alt="" width={56} height={56} loading="lazy" className="h-14 w-14 shrink-0 rounded-xl object-cover" />
            ) : (
              <span aria-hidden="true" className="h-14 w-14 shrink-0 rounded-xl bg-ocean-700/10" />
            )}
            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-2">
                <p className="font-semibold leading-snug text-ocean-950 dark:text-foam-50">{line.name}</p>
                <span className="shrink-0 font-semibold text-ocean-950 dark:text-foam-50">{formatInr(line.lineTotal)}</span>
              </div>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">
                {packById(line.pack).label} · {line.cut}
              </p>
              <p className="mt-0.5 text-xs font-bold text-teal-700 dark:text-teal-300">Qty: {line.qty}</p>
            </div>
          </li>
        );
      })}
    </ul>
  );
}

export function OrderTimeline({ order }: { order: Order }) {
  const cancelledAt = reachedAt(order, "cancelled");
  // The last step reached on the forward path (a cancelled order stops wherever it got to).
  const reachedSteps = STEPS.filter((step) => reachedAt(order, step.status));
  const current = reachedSteps[reachedSteps.length - 1]?.status;
  const visible = order.status === "cancelled" ? reachedSteps : STEPS;

  return (
    <ol aria-label="Order progress" className="space-y-4">
      {visible.map((step) => {
        const at = reachedAt(order, step.status);
        const done = at !== undefined;
        const isCurrent = order.status !== "cancelled" && step.status === current && order.status !== "delivered";
        return (
          <li key={step.status} className="flex gap-3" aria-current={isCurrent ? "step" : undefined}>
            <span
              aria-hidden="true"
              className={`mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${
                isCurrent ? "bg-teal-600 text-white ring-4 ring-teal-100" : done ? "bg-teal-600 text-white" : "bg-ocean-700/10 text-ocean-800/50 dark:bg-white/10"
              }`}
            >
              {done && !isCurrent ? <Check className="h-3.5 w-3.5" /> : <CircleDot className="h-3.5 w-3.5" />}
            </span>
            <div className={`min-w-0 flex-1 ${done ? "" : "opacity-70"}`}>
              <div className="flex flex-wrap items-baseline justify-between gap-x-3">
                <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">
                  {step.title}
                  {isCurrent && (
                    <>
                      {" "}
                      <span className="ml-1 rounded bg-teal-100 px-1.5 py-0.5 text-[11px] font-bold text-teal-700">Current step</span>
                    </>
                  )}
                </p>
                {at && <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{formatWhen(at)}</p>}
              </div>
              <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{step.detail}</p>
            </div>
          </li>
        );
      })}
      {order.status === "cancelled" && (
        <li className="flex gap-3" aria-current="step">
          <span aria-hidden="true" className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-coral-500 text-white">
            <Ban className="h-3.5 w-3.5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline justify-between gap-x-3">
              <p className="text-sm font-bold text-ocean-950 dark:text-foam-50">Cancelled</p>
              {cancelledAt && <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">{formatWhen(cancelledAt)}</p>}
            </div>
            <p className="text-xs text-ocean-800/80 dark:text-foam-100/75">This order was cancelled. Contact us if you have questions about your payment.</p>
          </div>
        </li>
      )}
    </ol>
  );
}

/** What happened to the money for a cancelled order. Nothing for orders that weren't cancelled. */
export function RefundNote({ order }: { order: Order }) {
  if (order.status !== "cancelled") return null;
  const refund = order.refund;
  return (
    <div role="status" className="space-y-1.5 rounded-xl bg-ocean-700/5 px-3 py-2 text-xs text-ocean-800/90 dark:bg-white/10 dark:text-foam-100/85">
      {refund?.status === "initiated" && (
        <p>
          <strong>Refund of {formatInr(refund.amountPaise / 100)} started</strong> on {formatWhen(refund.at)}. It goes back to your original payment
          method; your bank decides when it shows up.
          {refund.refundId && <> Reference <span className="font-mono">{refund.refundId}</span>.</>}
        </p>
      )}
      {refund?.status === "credited" && (
        <p>
          <strong>{formatInr(refund.amountPaise / 100)} credited to your ZenFresh wallet</strong> on {formatWhen(refund.at)}. It is there to use on your next order.
        </p>
      )}
      {refund?.status === "failed" && (
        <p>
          <strong>Your order is cancelled.</strong> We couldn&apos;t start the refund automatically, so ZenFresh will process it by hand. Contact us with
          order <span className="font-mono">{order.ref}</span> if you don&apos;t hear back.
        </p>
      )}
      {order.walletRefunded > 0 && (
        <p>
          <strong>{formatInr(order.walletRefunded)} of wallet credit returned</strong> — the part of this order you paid from your wallet is back in it.
        </p>
      )}
      {!refund && order.walletRefunded === 0 && (
        <p><strong>Your order is cancelled.</strong> Contact us with order <span className="font-mono">{order.ref}</span> about your payment.</p>
      )}
    </div>
  );
}
